import { createRouter, createWebHistory } from "vue-router";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import store from "@/store";
import Layout from "@/layout/Layout.vue";
import Home from "@/views/Home.vue";
import { ROUTES } from "@/constants/routes";

// Configuration NProgress
NProgress.configure({ showSpinner: false });

// Routes statiques
export const constantRoutes = [
    {
        path: "/",
        name: "Accueil",
        redirect: "/dashboard",
        component: Layout,
        children: [
            {
                path: "dashboard",
                name: "Dashboard",
                component: Home,
                meta: {
                    title: "Dashboard",
                    icon: "mdi:view-dashboard",
                    noCache: true
                }
            }
        ]
    },
    {
        path: "/profile/edit",
        component: Layout,
        hidden: true,
        children: [
            {
                path: "",
                name: "SelfProfile",
                component: () => import("@/views/users/SelfProfile.vue"),
                meta: { title: "Profil", icon: "user", noCache: true }
            }
        ]
    },
    {
        path: "/profile/notifications",
        component: Layout,
        hidden: true,
        children: [
            {
                path: "",
                name: "NotificationPreferences",
                component: () => import("@/views/profile/NotificationPreferences.vue"),
                meta: { title: "Préférences de notification", noCache: true }
            }
        ]
    },
    {
        // Historique complet de la cloche : atteignable depuis le popover, pas
        // depuis le menu (il n'a d'intérêt qu'après avoir vu une notification).
        path: "/notifications",
        component: Layout,
        hidden: true,
        children: [
            {
                path: "",
                name: "NotificationHistory",
                component: () => import("@/views/notifications/NotificationHistory.vue"),
                meta: { title: "Historique des notifications", noCache: true }
            }
        ]
    },
    {
        path: ROUTES.LOGIN,
        name: "Login",
        component: () => import("@/views/Login.vue")
    },
    {
        path: ROUTES.FORGOT_PASSWORD,
        name: "ForgotPassword",
        component: () => import("@/views/ForgotPassword.vue")
    },
    {
        path: ROUTES.RESET_PASSWORD,
        name: "ResetPassword",
        component: () => import("@/views/ResetPassword.vue")
    },
    {
        path: "/404",
        name: "404",
        component: () => import("@/views/error-page/404.vue"),
        hidden: true
    }
];

// Routes asynchrones importées
import adminRoutes from "./modules/admin";
import prospectRoutes from "./modules/prospect";
import extranetRoutes from "./modules/extranet";
import automaticdebitsRoutes from "./modules/automaticDebits";
import renouvelementRoutes from "./modules/renouvelement";
import compareCA from "./modules/compareCA";
import gestionNumFixe from "./modules/gestionNumFixe";
import stats from "./modules/stats";
import tariffmanagementRoute from "./modules/tariffmanagement";
import hostingRoutes from "./modules/hosting";
import stockRoutes from "./modules/stock";
import telephonyRoutes from "./modules/telephony";
import provisioningRoutes from "./modules/provisioning";
import projetsRoutes from "./modules/projets";
import processusRoutes from "./modules/processus";
import supportRoutes from "./modules/support";
import mobiluceRoutes from "./modules/mobiluce";
import facturesAchatRoutes from "./modules/facturesAchat";
import comptaParametrageRoutes from "./modules/comptaParametrage";
import comptaDashboardRoutes from "./modules/comptaDashboard";
import aiRoutes from "./modules/ai";
import documentationRoutes from "./modules/documentation";
// Offres commerciales masquées temporairement (feature non finalisée).
// Le module « tâches » qui les accompagnait a été retiré : il est remplacé par
// la gestion de projet (@/views/projets), seule à porter les tâches désormais.
// import {
//     offresRoutes,
//     offresAdminRoutes
// } from "./modules/offres";

// Regroupements de la sidebar. Ce sont des nœuds purement présentationnels
// (`meta.is_group`, sans `component` ni URL routable) : le store `permission`
// les remonte au niveau racine du routeur (`expandGroups`), donc les URLs,
// le breadcrumb et les permissions des modules restent strictement inchangés.
// La barre latérale déroule leurs pages sur un seul niveau (`menuTree`).
const group = (path, title, icon, children) => ({
    path,
    meta: { title, icon, is_group: true },
    children
});

// Un module n'apparaît qu'une fois : rangé dans un groupe, il ne se ré-ajoute
// pas à la racine (double entrée sidebar + "duplicate named route").
export const asyncRoutes = [
    extranetRoutes,
    prospectRoutes,
    tariffmanagementRoute,
    group("/g-facturation", "Facturation", "mdi:receipt-text-outline", [
        renouvelementRoutes,
        automaticdebitsRoutes
    ]),
    group("/g-comptabilite", "Comptabilité", "mdi:calculator-variant-outline", [
        comptaDashboardRoutes,
        facturesAchatRoutes,
        comptaParametrageRoutes
    ]),
    stockRoutes,
    group("/g-telephonie", "Téléphonie", "mdi:phone-in-talk", [
        telephonyRoutes,
        gestionNumFixe
    ]),
    hostingRoutes,
    provisioningRoutes,
    group("/g-projets", "Projets", "mdi:view-dashboard-outline", [
        projetsRoutes,
        processusRoutes
    ]),
    supportRoutes,
    group("/g-statistiques", "Statistiques", "mdi:poll", [
        compareCA,
        stats
    ]),
    aiRoutes,
    documentationRoutes,
    mobiluceRoutes,
    // offresRoutes,
    // offresAdminRoutes,
    adminRoutes
];

// Création du router
const router = createRouter({
    history: createWebHistory(),
    routes: constantRoutes,
    scrollBehavior: () => ({ y: 0 })
});

// Reset router pour logout
export function resetRouter() {
    const newRouter = createRouter({
        history: createWebHistory(),
        routes: constantRoutes
    });
    router.matcher = newRouter.matcher;
}

// Ajout de routes dynamiques
export function addDynamicRoutes(routes) {
    routes.forEach(route => router.addRoute(route));
}

// Guard global
router.beforeEach(async (to, from, next) => {
    NProgress.start();
    const token = store.state.user.token;

    if (token) {
        const roles = store.getters["user/roles"] || [];
        if (!roles.length) {
            try {
                const userInfo = await store.dispatch("user/getInfo");
                const accessedRoutes = await store.dispatch(
                    "permission/generateRoutes",
                    {
                        roles: userInfo.roles || [],
                        permissions: userInfo.permissions || [],
                        // Côté calculé par le serveur, déjà posé dans le state par
                        // `getInfo` (avec repli sur l'ancien nom de rôle).
                        isExtranet: store.getters["user/isExtranet"] === true
                    },
                    { root: true }
                );

                accessedRoutes.forEach(route => router.addRoute(route));
                router.addRoute({
                    path: "/:catchAll(.*)",
                    redirect: "/404",
                    hidden: true
                });
                next({ ...to, replace: true });
            } catch (err) {
                await store.dispatch("user/resetToken");
                next({ path: ROUTES.LOGIN, query: { redirect: to.fullPath } });
            }
        } else {
            next();
        }
    } else {
        const publicPaths = [
            ROUTES.LOGIN,
            ROUTES.FORGOT_PASSWORD,
            ROUTES.RESET_PASSWORD,
            "/404"
        ];
        if (publicPaths.includes(to.path)) {
            next();
        } else {
            const isPublic = asyncRoutes.some(route => {
                if (route.path === to.path || to.path.startsWith(route.path + '/')) {
                    if (route.meta && route.meta.public) return true;
                    if (route.children) {
                        return route.children.some(child => child.meta && child.meta.public);
                    }
                }
                return false;
            });
            if (isPublic) {
                let added = false;
                asyncRoutes.forEach(route => {
                    if (route.meta && route.meta.public) {
                        const existing = router.getRoutes().find(r => r.name === route.name || r.path === route.path);
                        if (!existing) {
                            router.addRoute(route);
                            added = true;
                        }
                    } else if (route.children) {
                        const hasPublicChild = route.children.some(child => child.meta && child.meta.public);
                        if (hasPublicChild) {
                            const existing = router.getRoutes().find(r => r.name === route.name || r.path === route.path);
                            if (!existing) {
                                router.addRoute(route);
                                added = true;
                            }
                        }
                    }
                });
                if (!router.getRoutes().find(r => r.path === "/:catchAll(.*)")) {
                    router.addRoute({
                        path: "/:catchAll(.*)",
                        redirect: "/404",
                        hidden: true
                    });
                }
                if (added) {
                    next({ ...to, replace: true });
                } else {
                    next();
                }
            } else {
                next({ path: ROUTES.LOGIN, query: { redirect: to.fullPath } });
            }
        }
    }
});
router.afterEach((to) => {
    document.title = to.meta?.title ? `${to.meta.title} - Prosoluce` : "Gestion - Prosoluce";
    NProgress.done();
});

// Après un nouveau déploiement, les anciens chunks JS/CSS référencés par un onglet
// ouvert n'existent plus ; Nginx renvoie index.html en fallback et le navigateur
// échoue à parser du HTML comme du JS. On recharge la page pour récupérer le
// nouveau manifest et les nouveaux hash.
const CHUNK_ERROR_REGEX = /Loading (CSS )?chunk [^\s]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const CHUNK_RELOAD_KEY = "__chunk_reload_attempted_at__";

function reloadOnChunkError(error, to) {
    const message = error?.message || "";
    if (!CHUNK_ERROR_REGEX.test(message)) return false;

    // Garde-fou contre une boucle infinie si le nouveau déploiement est cassé.
    const now = Date.now();
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (now - last < 10000) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));

    if (to?.fullPath) {
        window.location.assign(to.fullPath);
    } else {
        window.location.reload();
    }
    return true;
}

router.onError((error, to) => {
    reloadOnChunkError(error, to);
});

// Secondaire : certaines erreurs remontent via unhandledrejection si onError
// ne capture pas (cas rare des imports dynamiques hors navigation).
window.addEventListener("unhandledrejection", (event) => {
    reloadOnChunkError(event?.reason);
});

export default router;
